import { useEffect, useState } from 'react';

import { styled } from '@mui/material/styles';
import { useLocalStorage } from '../../utils/useLocalStorage';
import { useFetchService } from '../../utils/fetchService';
import { PageLoader } from '../../PageLoader';
import { AuthPermissions } from '../../auth/AuthPermissions';
import { verifyAuthPermission } from '../../auth/VerifyAuthPermission';
import { OktaIntegrationCard } from './OktaIntegrationCard';
// eslint-disable-next-line import/extensions
import { SettingsMode } from './enum/SettingsMode.enum';
import { IntuneIntegrationCard } from './IntuneIntegrationCard';
import { Events, QuokkaEvent, useSubscribeToEvent } from '../../utils/pubSub';
import { HexnodeIntegrationCard } from './HexnodeIntegrationCard';
import { IvantiIntegrationCard } from './IvantiIntegrationCard';
import { SentinelIntegrationCard } from './SentinelIntegrationCard';
import { WorkspaceOneIntegrationCard } from './WorkspaceOneIntegrationCard';
import { MDM_INTEGRATION_SETTINGS_ID } from '../shared/constants';

export const useIdentityProviders = (accessToken: string) => {
  const { data: idpApiCredentials, error: idpError, mutate } = useFetchService('/organizations/identity-providers', accessToken);
  const isLoading = !idpApiCredentials && !idpError;
  return { idpApiCredentials, idpError, isLoading, mutate };
};

const useMdmIntegrations = (accessToken: string, id: number) => {
  const { data: settings, error, mutate } = useFetchService(`/organizations/mdm-integrations/${id}`, accessToken);
  const isLoading = !settings && !error;
  return { settings, error, isLoading, mutate };
};

export const useSentinelIntegration = (accessToken: string) => {
  const response = useFetchService('/organizations/sentinel-integration-service/api-key/get', accessToken);
  const { data: sentinelServiceData, error: sentinelError } = response;
  const isLoadingSentinel = !sentinelServiceData && !sentinelError;
  return { sentinelServiceData, sentinelError, isLoadingSentinel };
};

export const useUsersList = (accessToken: string) => {
  const { data, error, mutate } = useFetchService('/organizations/users/list', accessToken);
  const isLoading = !data && !error;

  return { data, error, isLoading, mutate };
};

export function Integrations() {
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const [mode, setMode] = useState({
    okta: SettingsMode.READ,
    intune: SettingsMode.READ,
    hexnode: SettingsMode.READ,
    ivanti: SettingsMode.READ,
    workspaceOne: SettingsMode.READ,
  });
  const { idpApiCredentials = {}, idpError, isLoading, mutate: mutateOkta } = useIdentityProviders(accessToken);
  const {
    settings: intuneSettings = [],
    error: intuneError,
    isLoading: isLoadingIntuneSettings,
    mutate: mutateIntune,
  } = useMdmIntegrations(accessToken, MDM_INTEGRATION_SETTINGS_ID.INTUNE);
  const { settings: hexnodeSettings = [], mutate: mutateHexnode } = useMdmIntegrations(accessToken, MDM_INTEGRATION_SETTINGS_ID.HEXNODE);
  const { settings: ivantiSettings = [], mutate: mutateIvanti } = useMdmIntegrations(accessToken, MDM_INTEGRATION_SETTINGS_ID.IVANTI);
  const { settings: workspaceOneSettings = [], mutate: mutateWorkspaceOne } = useMdmIntegrations(
    accessToken,
    MDM_INTEGRATION_SETTINGS_ID.WORKSPACE_ONE,
  );
  const { sentinelServiceData = {}, isLoadingSentinel } = useSentinelIntegration(accessToken);

  // subscribe to when INTUNE sync is done successfully so we can cause a re-render displaying the latest info
  useSubscribeToEvent(Events.INTUNE_SYNC_SUCCESSFUL);
  useSubscribeToEvent(Events.HEXNODE_SYNC_SUCCESSFUL);
  useSubscribeToEvent(Events.IVANTI_SYNC_SUCCESSFUL);
  useSubscribeToEvent(Events.WORKSPACE_ONE_SYNC_SUCCESSFUL);

  useEffect(() => {
    QuokkaEvent.on(Events.INTUNE_SYNC_SUCCESSFUL, eventData => {
      // calling this function invalidates the cache and causes a re-render
      mutateIntune();
    });
    QuokkaEvent.on(Events.HEXNODE_SYNC_SUCCESSFUL, eventData => {
      mutateHexnode();
    });
    QuokkaEvent.on(Events.IVANTI_SYNC_SUCCESSFUL, eventData => {
      mutateIvanti();
    });
    QuokkaEvent.on(Events.WORKSPACE_ONE_SYNC_SUCCESSFUL, eventData => {
      mutateWorkspaceOne();
    });
  }, []);

  const canUpdateOrg = verifyAuthPermission(AuthPermissions.UPDATE_ORG);

  if (idpError || intuneError) {
    return <div>failed to load</div>;
  }

  function getSettingValue(settingsArray: Array<{ key: string; value: string }>, key: string, defaultValue: string = '') {
    const setting = settingsArray.find(settingItem => settingItem.key === key);
    return setting ? setting.value : defaultValue;
  }

  return (
    <>
      {isLoading || isLoadingIntuneSettings ? (
        <PageLoader />
      ) : (
        <StyledCardContainer>
          <OktaIntegrationCard
            signInUrlPlaceholder={idpApiCredentials?.[0]?.signInUrl ?? ''}
            managementHintPlaceholder={{
              ios: idpApiCredentials?.[0]?.iosManagementHint ?? '',
              android: idpApiCredentials?.[0]?.androidManagementHint ?? '',
            }}
            isAdmin={canUpdateOrg}
            mode={mode}
            setMode={setMode}
            mutate={mutateOkta}
          />
          <IntuneIntegrationCard
            baseUrlPlaceholder={getSettingValue(intuneSettings, 'base_url', 'https://graph.microsoft.com')}
            clientIdPlaceholder={getSettingValue(intuneSettings, 'client_id')}
            clientSecretPlaceholder={getSettingValue(intuneSettings, 'client_secret')}
            tenantIdPlaceholder={getSettingValue(intuneSettings, 'tenant_id')}
            lastSync={getSettingValue(intuneSettings, 'last_sync')}
            isAdmin={canUpdateOrg}
            mode={mode}
            setMode={setMode}
            mutate={mutateIntune}
          />
          <HexnodeIntegrationCard
            apiKeyPlaceholder={getSettingValue(hexnodeSettings, 'api_key')}
            portalPlaceholder={getSettingValue(hexnodeSettings, 'portal')}
            lastSync={getSettingValue(hexnodeSettings, 'last_sync')}
            isAdmin={canUpdateOrg}
            mode={mode}
            setMode={setMode}
            mutate={mutateHexnode}
          />
          <IvantiIntegrationCard
            usernamePlaceholder={getSettingValue(ivantiSettings, 'username')}
            passwordPlaceholder={getSettingValue(ivantiSettings, 'password')}
            lastSync={getSettingValue(ivantiSettings, 'last_sync')}
            isAdmin={canUpdateOrg}
            mode={mode}
            setMode={setMode}
            mutate={mutateIvanti}
          />
          <WorkspaceOneIntegrationCard
            subDomainPlaceholder={getSettingValue(workspaceOneSettings, 'subdomain')}
            regionPlaceholder={getSettingValue(workspaceOneSettings, 'region')}
            clientIdPlaceholder={getSettingValue(workspaceOneSettings, 'client_id')}
            clientSecretPlaceholder={getSettingValue(workspaceOneSettings, 'client_secret')}
            lastSync={getSettingValue(workspaceOneSettings, 'last_sync')}
            isAdmin={canUpdateOrg}
            mode={mode}
            setMode={setMode}
            mutate={mutateWorkspaceOne}
          />
          {!isLoadingSentinel && (
            <SentinelIntegrationCard
              sentinelApiKey={sentinelServiceData?.sentinelApiKey}
              organizationId={sentinelServiceData?.organizationId}
            />
          )}
        </StyledCardContainer>
      )}
    </>
  );
}

const StyledCardContainer = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  width: 100%;

  @media (max-width: 1650px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;
